/**
 * Search Results Dropdown Component
 * Story 11.5: Dropdown showing grouped search results
 */

'use client';

import React from 'react';
import * as LucideIcons from 'lucide-react';
import {
  getEntityTypeIcon,
  getEntityTypeColor,
  getEntityTypeDisplayName,
  getSearchResultLink,
  type SearchResponse,
} from '@/hooks/useGlobalSearch';

interface SearchResultsDropdownProps {
  query: string;
  data?: SearchResponse;
  isLoading: boolean;
  onResultClick: () => void;
  onViewAll: () => void;
}

export function SearchResultsDropdown({
  query,
  data,
  isLoading,
  onResultClick,
  onViewAll,
}: SearchResultsDropdownProps) {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10-lg border border-gray-200 dark:border-white/10 p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10-lg border border-gray-200 dark:border-white/10 p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <LucideIcons.Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">No results found for "{query}"</p>
          <p className="text-xs mt-1">Try a different search term</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-white/10-lg border border-gray-200 dark:border-white/10 overflow-hidden">
      {/* Search results by type */}
      <div className="max-h-96 overflow-y-auto">
        {data.results.map((group) => (
          <SearchResultGroup
            key={group.entity_type}
            group={group}
            query={query}
            onResultClick={onResultClick}
          />
        ))}
      </div>

      {/* Footer with "View all results" */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-brand-dark/50">
        <button
          onClick={onViewAll}
          className="w-full text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          View all {data.total} result{data.total !== 1 ? 's' : ''}
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
          Search completed in {data.query_time_ms}ms
        </p>
      </div>
    </div>
  );
}

interface SearchResultGroupProps {
  group: {
    entity_type: string;
    results: Array<{
      id: string;
      type: string;
      title: string;
      description?: string;
      highlight?: string;
      score: number;
      metadata?: Record<string, any>;
    }>;
    count: number;
  };
  query: string;
  onResultClick: () => void;
}

function SearchResultGroup({ group, query, onResultClick }: SearchResultGroupProps) {
  const displayName = getEntityTypeDisplayName(group.entity_type);
  const Icon = (LucideIcons as any)[getEntityTypeIcon(group.entity_type)] || LucideIcons.FileText;
  const colorClass = getEntityTypeColor(group.entity_type);

  // Limit to 3 results per type
  const displayResults = group.results.slice(0, 3);
  const hasMore = group.count > 3;

  return (
    <div className="border-b border-gray-200 dark:border-white/10 last:border-b-0">
      {/* Group header */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-brand-dark/50">
        <div className="flex items-center gap-2">
          <div className={`p-1 rounded ${colorClass}`}>
            <Icon size={14} />
          </div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            {displayName} ({group.count})
          </h4>
        </div>
      </div>

      {/* Results */}
      <div>
        {displayResults.map((result) => (
          <SearchResultItem
            key={result.id}
            result={result}
            onResultClick={onResultClick}
          />
        ))}
      </div>

      {/* "View all" for this type if there are more */}
      {hasMore && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-brand-dark/50">
          <button
            onClick={onResultClick}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            View all {group.count} {displayName.toLowerCase()} results
          </button>
        </div>
      )}
    </div>
  );
}

interface SearchResultItemProps {
  result: {
    id: string;
    type: string;
    title: string;
    description?: string;
    highlight?: string;
    score: number;
    metadata?: Record<string, any>;
  };
  onResultClick: () => void;
}

function SearchResultItem({ result, onResultClick }: SearchResultItemProps) {
  const Icon = (LucideIcons as any)[getEntityTypeIcon(result.type)] || LucideIcons.FileText;
  const colorClass = getEntityTypeColor(result.type);
  const link = getSearchResultLink(result);

  const handleClick = () => {
    if (link) {
      window.location.href = link;
    }
    onResultClick();
  };

  return (
    <button
      onClick={handleClick}
      className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
    >
      {/* Icon */}
      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
        <Icon size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title with highlighted text */}
        <p
          className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1"
          dangerouslySetInnerHTML={{ __html: result.title }}
        />

        {/* Description or highlight */}
        {(result.highlight || result.description) && (
          <p
            className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5"
            dangerouslySetInnerHTML={{
              __html: result.highlight || result.description || '',
            }}
          />
        )}

        {/* Metadata */}
        {result.metadata && (
          <div className="flex items-center gap-2 mt-1">
            {result.metadata.status && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                {result.metadata.status}
              </span>
            )}
            {result.metadata.created_at && (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {new Date(result.metadata.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Relevance score (hidden by default, shown on hover for debugging) */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-50 transition-opacity">
        <span className="text-xs text-gray-400 dark:text-gray-600">
          {Math.round(result.score * 100)}%
        </span>
      </div>
    </button>
  );
}

export default SearchResultsDropdown;
